import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { selectFiduciaryFeature } from '@apps/fiduciary/fiduciary.selectors';
import { selectNavigatedCustomerSpace } from '@core/core.selectors';
import { Store, select } from '@ngrx/store';
import { selectPortalConfig } from '@platform/platform.selectors';
import { ResourceStatuses } from '@platform/shared/resource.state';
import { areFilesValid } from '@platform/shared/utilities/file-utility';
import { ToastService } from '@platform/toast/toast.service';
import { RootState } from '@root/app.state';
import { Observable, map, filter } from 'rxjs';
import * as FiduciaryActions from '@apps/fiduciary/fiduciary.actions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-creditor-widget',
  templateUrl: './creditor-widget.component.html',
  styleUrls: ['./creditor-widget.component.scss'],
  standalone: false,
})
export class CreditorWidgetComponent implements OnInit {
  @Input({ required: true }) customerSpaceId!: string;
  public isDragAndDropOverlayVisible = false;

  public uploadPending$!: Observable<boolean>;

  private allowedFileFormats: string[] = [];
  private invalidFileAndFolderNameCharacters: string[] = [];
  private destroyRef = inject(DestroyRef);

  constructor(
    private store$: Store<RootState>,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.uploadPending$ = this.store$.pipe(
      select(selectFiduciaryFeature),
      map((state) => state.abacusInboxes.creditor),
      map((state) => state.status === ResourceStatuses.PENDING),
    );

    this.store$
      .pipe(
        select(selectPortalConfig),
        filter((config) => !!config),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((x) => {
        this.allowedFileFormats = x!.allowedFileUploadFormatsInbox;
        this.invalidFileAndFolderNameCharacters = x!.invalidFileAndFolderNameCharacters;
      });

    this.store$
      .pipe(
        select(selectNavigatedCustomerSpace),
        filter((state) => !!state.data),
        map((state) => state.data),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((x) => (this.customerSpaceId = x.id));
  }

  public uploadCreditorDocuments(files: FileList) {
    const filesValid = areFilesValid(files, this.invalidFileAndFolderNameCharacters, this.allowedFileFormats);

    if (!filesValid.valid) {
      this.toastService.showErrorToast(filesValid.error!.translationKey, filesValid.error?.interpolateParams);
      return;
    }

    this.store$.dispatch(
      FiduciaryActions.uploadFiduciaryInboxFiles({
        customerSpaceId: this.customerSpaceId,
        files,
        targetInbox: 'creditor',
      }),
    );
  }

  public showDragAndDropOverlay() {
    this.isDragAndDropOverlayVisible = true;
  }

  public hideDragAndDropOverlay() {
    this.isDragAndDropOverlayVisible = false;
  }

  public uploadFile(event: FileList) {
    this.uploadCreditorDocuments(event);
    this.hideDragAndDropOverlay();
  }
}
